'use client';
import { FormEvent, FunctionComponent, useCallback, useId, useMemo, useState } from 'react';
import { PublicEducator } from '@cademy-package/sdk-component-types';
import { CademyError } from '@shared/domain-shared';
import { useCaptcha } from '../../../../../hooks/useCaptcha';
import styles from '../styles.module.scss';
import { Alert } from '../../../../../components/Alert';
import Button from '../../../../../components/Button';
import { PaperPlaneIcon } from '../../../../../components/Icons';
import { CourseCarousel } from '../../../../../components/CourseCarousel';
import { useEducatorProfileLinks } from '../../../../../hooks/useEducatorProfileLinks';
import {
    EmailField,
    FormContext,
    PhoneField,
    TextAreaField,
    TextField,
    DropdownField,
    useForm,
} from '@cademy-ui/form';
import { useAnalytics } from '../../../../../hooks/Analytics/useAnalytics';
import { MarketplaceAndMinisiteOnly } from '../../../../../components/AppContext/MarketplaceAndMinisiteOnly';
import { useAppContext } from '../../../../../contexts/AppContext/useAppContext';
import { useSearchParams } from 'next/navigation';
import { VatSettings } from '@shared/types';

export type EnquiryFormProps = {
    educatorId: string;
    educatorName: string;
    educatorSlug: string;
    enquiryTypes: NonNullable<PublicEducator['contact_form_options']>;
    educatorVATSettings?: VatSettings;
};

type EnquriyFormResponse = {
    enquiryType?: string;
    firstName: string;
    lastName: string;
    email: string;
    telephone: string;
    message: string;
};

export const EnquiryForm: FunctionComponent<EnquiryFormProps> = ({
    educatorId,
    educatorName,
    educatorSlug,
    enquiryTypes,
    educatorVATSettings,
}) => {
    const id = useId();
    const appContext = useAppContext();
    const [captchaElement, getCaptchaToken] = useCaptcha();
    const { coursesLink } = useEducatorProfileLinks(educatorSlug);
    const hasEnquiryOptions = useMemo(() => {
        return enquiryTypes.length > 0;
    }, [enquiryTypes]);

    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<CademyError | null>(null);
    const searchParams = useSearchParams();
    const courseDetails = useMemo(() => {
        if (!searchParams) return {};
        const course_slug = searchParams.get('course_ref');
        const course_name = searchParams.get('course_name');
        if (!course_slug || !course_name) return {};
        return { course_slug, course_name };
    }, [searchParams]);

    const Analytics = useAnalytics();

    const onFormSubmit = useCallback(
        async (formResponse: Partial<EnquriyFormResponse>) => {
            if (isSubmitting) {
                return;
            }
            setIsSubmitting(true);
            setError(null);
            try {
                const token = await getCaptchaToken();

                const response = await fetch(
                    `${process.env.NEXT_PUBLIC_CADEMY_API_URL}/enquiries`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            contact_details: {
                                first_name: formResponse.firstName,
                                last_name: formResponse.lastName,
                                email: formResponse.email,
                                phone: formResponse.telephone,
                            },
                            details: {
                                message: formResponse.message,
                                embedded: appContext === 'embed',
                                ...courseDetails,
                                ...(hasEnquiryOptions
                                    ? { enquiry_type: formResponse.enquiryType }
                                    : {}),
                            },
                            type: 'message',
                            educator_slug: educatorSlug,
                            captchaToken: token,
                        }),
                        credentials: 'include',
                    }
                );
                if (response.ok === false) {
                    if (
                        response.headers.get('Content-Type') === 'application/json' ||
                        response.headers.get('Content-Type') === 'application/problem+json'
                    ) {
                        const error = await response.json();
                        throw error;
                    }
                    const error = await response.text();
                    throw error;
                }
                Analytics.record.event('Enquriy Sumbitted', {
                    educator_ref: educatorId,
                    educator_name: educatorName,
                });
                setHasSubmitted(true);
            } catch (error) {
                setError(CademyError.fromUnknown(error));
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            educatorId,
            educatorName,
            educatorSlug,
            getCaptchaToken,
            isSubmitting,
            hasEnquiryOptions,
            Analytics,
            appContext,
            courseDetails,
        ]
    );

    const formContext = useForm<EnquriyFormResponse>({
        initialValues: {},
        onSubmit: onFormSubmit,
        requiredStrategy: 'required',
        submitWhenInvalid: false,
    });

    const onSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            return formContext.submit();
        },
        [formContext]
    );

    if (hasSubmitted) {
        return (
            <article className={styles.successSplashScreen}>
                <h3>Thanks for contacting us</h3>
                <p>
                    Thank you <b>{formContext.values.firstName}</b> for contacting us. We have
                    received your message and someone will be in touch soon.
                </p>
                <MarketplaceAndMinisiteOnly>
                    <CourseCarousel
                        filters={{ educator_ref: [educatorId] }}
                        endCardText={`View all listings by ${educatorName}`}
                        endCardLink={coursesLink}
                        name={`More listings from ${educatorName}`}
                        mediaQueries={{
                            '(min-width: 560px)': {
                                numberOfVisible: 2,
                            },
                        }}
                        vatSettings={educatorVATSettings}
                    />
                </MarketplaceAndMinisiteOnly>
            </article>
        );
    }

    return (
        <FormContext.Provider value={formContext}>
            <form
                id={id}
                name={`Contact ${educatorName}`}
                className={styles.form}
                autoComplete="on"
                onSubmit={onSubmit}
                aria-labelledby={`${id}-header`}
                aria-describedby={`${id}-header-helptext`}
            >
                <header>
                    <h3 id={`${id}-header`}>Send a message</h3>
                    <p
                        id={`${id}-header-helptext`}
                    >{`Contact ${educatorName} to ask any questions or clarify any details.`}</p>
                </header>

                {error ? <Alert preset="danger">{error.detail || error.title}</Alert> : null}

                {hasEnquiryOptions ? (
                    <DropdownField
                        label="Enquiry Type"
                        name="enquiryType"
                        required
                        options={enquiryTypes.map((enquiryOption) => {
                            return {
                                label: enquiryOption.label,
                                value: enquiryOption.label,
                            };
                        })}
                    />
                ) : null}
                <TextField label="First name" name="firstName" required />
                <TextField label="Last name" name="lastName" required />
                <EmailField label="Email" name="email" required />
                <PhoneField name="telephone" label="Telephone" required />
                <TextAreaField name="message" label="Message" required />
                {captchaElement}
                <Button
                    className={styles.submit}
                    preset="primary"
                    type="submit"
                    disabled={isSubmitting}
                >
                    <PaperPlaneIcon /> Send message
                </Button>
            </form>
        </FormContext.Provider>
    );
};
